import React, { useContext, useEffect, useState } from "react";
import { Modal, Notification, STATUS_TYPES, TextForm, TextingControls, Person, Icon, generateResolver, productRestriction, PRODUCT_PERMISSIONS } from "dyl-components";
import { Button, Form, Segment } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";

import textActions from "actions/text";
import text_templatesActions from "actions/text_templates";

import "./TextMessageModal.scss";
import { FormProvider, useController, useForm } from "react-hook-form";
import { FileUtils, PhoneUtil, StringUtils } from "utils";
import uploadActions from "actions/upload";
import PHONE_ICON from "shared/constants/PHONE_ICON";
import { convertRecipientAccountVariables, convertRecipientContactVariables, convertSenderVariables } from "utils/TextFormUtils";
import { FILE_CATEGORIES } from "utils/FileUtils";
import CategorizedTemplates from "shared/NewTasksTab/subcomponents/CategorizedTemplates";
import templateCategoryActions from "actions/template_category"
import tcrActions from "actions/tcr"
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";
import { TextingComponentContext } from "shared/context/TextingComponentProvider";
import sequenceTaskActions from "actions/sequence_tasks"

const TextFormContainer = ({ contact_id, contact_name, isAccount, setIsTextFormDirty, defaultValues, selectedSequence, onMessageSend, onClose }) => {
    const dispatch = useDispatch();
    const isSendingText = useSelector(state => state.text.isSendingText || state.upload.isUploadingFile);
    const brandOptOutStatus = useSelector(state => state.tcr.brandOptOutStatus);
    const textPhones = useSelector(state => state.text.phones?.map(phone => ({
        key: phone,
        value: phone,
        text: PhoneUtil.formatPhoneNumber(phone)
    })) || []);
    const account = useSelector(state => state.text.account);
    const contact = useSelector(state => state.text.contact);
    const userProfile = useSelector(state => state.user.userProfile);
    const recipientPhones = isAccount ? account?.phones : contact?.phone;
    const mainPhone = recipientPhones?.find(phone => phone.main) || recipientPhones?.[0];
    const isCalendarView = !!selectedSequence?.id
    const productPermissions = useSelector(
        (state) => state.subscription.productPermissions
    );
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);

    const replaceVariables = (text) => {
        if (!text) return text;
        try {
            return convertRecipientContactVariables(convertSenderVariables(text, userProfile || {}), contact);
        } catch (e) {
            console.error('replaceVariables error:', e);
            return text;
        }
    };

    const methods = useForm({
        defaultValues: { ...defaultValues, message: replaceVariables(defaultValues?.message) || '', from: textPhones[0]?.value || '', to: mainPhone?.phone || '' } || {
            message: '',
            attachments: [],
            include_opt_out: false,
            from: textPhones[0]?.value || '',
            to: mainPhone?.phone || ''
        },
        mode: 'onChange',
        resolver: generateResolver({
            attachments: FileUtils.getFilesizeValidator(FILE_CATEGORIES.TEXT),
        })
    });
    const { control, reset, formState: { isValid, isDirty }, handleSubmit } = methods;
    const { field: toField } = useController({
        name: 'to',
        control
    });
    const { field: fromField } = useController({
        name: 'from',
        control
    })
    const {
        field: messageField
    } = useController({
        name: 'message',
        control
    });
    const {
        field: attachmentsField
    } = useController({
        name: 'attachments',
        control
    });

    useEffect(() => {
        setIsTextFormDirty(methods.formState.isDirty)
    }, [methods.formState.isDirty, setIsTextFormDirty])

    useEffect(() => {
        reset({
            include_opt_out: selectedSequence?.action_data?.include_opt_out || false,
        })
    }, [selectedSequence, reset])


    const handleSelectTemplateChange = (value) => {
        if (value) {
            dispatch(text_templatesActions.getTemplate(value))
                .then(({ message, attachments }) => {
                    const formattedMessage = convertSenderVariables(message || '', userProfile);
                    messageField.onChange({
                        target: {
                            name: messageField.name,
                            value: isAccount ? convertRecipientAccountVariables(formattedMessage, account) : convertRecipientContactVariables(formattedMessage, contact)
                        }
                    });
                    const mappedAttachments = (attachments?.data || []).map(att => ({
                        id: att.file_id,
                        name: att.name,
                        size: att.size,
                        url: att.url,
                        path: att.name
                    }));

                    attachmentsField.onChange({
                        target: {
                            name: attachmentsField.name,
                            value: mappedAttachments
                        }
                    });
                }).catch(error => {
                    console.log(error);
                });
        }
    };

    const handleSendText = async (data) => {
        const from = `${data.from}`;
        const to = `${data.to}`;
        const message = data.message;
        try {
            const textBody = {
                from,
                to,
                message: isAccount ? convertRecipientAccountVariables(message, account) : convertRecipientContactVariables(message, contact),
                contact_id: Number(contact_id),
                opt_out_message: data.include_opt_out || false
            };
            const attachments = data.attachments || [];
            const templateAttachments = attachments.filter(att => att.id).map(att => ({ id: att.id, url: att.url }));
            const newAttachments = attachments.filter(att => !att.id);
            const uploadedAttachments = await (async () => {
                if (newAttachments.length) {
                    const files = await dispatch(uploadActions.uploadFiles(newAttachments, 'text', [], contact_id, true));
                    return files.map(file => ({ id: file.id, url: file.url }));
                }
                return [];
            })();
            await dispatch(textActions.sendText({
                ...textBody,
                attachments: [...templateAttachments, ...uploadedAttachments]
            }))
            if (isCalendarView && selectedSequence?.id) {
                const sequenceTaskType = (() => {
                    if (!selectedSequence?.contact?.sequence_id) {
                        return ""
                    }
                    if (selectedSequence?.contact?.pipeline_category === "customer") {
                        return "customer"
                    }
                    return "module"
                })()
                await dispatch(
                    sequenceTaskActions.complete(selectedSequence?.id, { status: "completed", sequence_task_type: sequenceTaskType, pipeline_id: selectedSequence?.contact?.pipeline_id })
                );
                onMessageSend && onMessageSend();
            }
            Notification.alert('Sucessfully sent message!', STATUS_TYPES.SUCCESS);
            dispatch(textActions.getFilteredMessages({ contact_phone: to, customer_phone: from }));
            reset({ message: '', to, from: from, attachments: [] });
            onClose && onClose();
        } catch (error) {
            console.log(error);
            if (error?.Message === "fund is not enough") {
                Notification.alert('SMS reload balance is required.', STATUS_TYPES.ERROR);
            } else {
                Notification.alert('Failed to send message', STATUS_TYPES.ERROR);
            }
        }
    };

    const isReadingCategorizedTemplates = useSelector(state => state.template_category.isReadingCategorizedTemplates);
    return (
        <>
            <Modal.Content scrolling>
                <FormProvider {...methods}>
                    <Form>
                        <TextingControls 
                            basic 
                            disabled={isCalendarView}
                            recipientPhones={
                            mainPhone ? [
                                    {
                                        key: mainPhone.id,
                                        value: mainPhone.phone,
                                        text: PhoneUtil.formatPhoneNumber(mainPhone.phone),
                                        content: (
                                            <div className='ChatBox__dropdownOption--to'>
                                                <Icon name={PHONE_ICON[StringUtils.capitalize(mainPhone.phone_type)] || 'phone'} /> {PhoneUtil.formatPhoneNumber(mainPhone.phone)}  {mainPhone.main && <Icon
                                                    className="fas fa-star"
                                                    color="primary"
                                                />}
                                            </div>
                                        ),
                                        selectedOptionFormat: (<Person
                                            username={contact_name}
                                            email={PhoneUtil.formatPhoneNumber(mainPhone.phone)}
                                            subtitleLabel='Phone'
                                        />)
                                    }
                                ] : []
                            } 
                            senderPhones={textPhones} 
                            isInline={false} 
                            to={toField.value} 
                            from={fromField.value}
                            onChangeRecipient={(_, { value }) => { toField.onChange({ target: { name: toField.name, value } }) }} 
                            onChangeSender={(_, { value }) => { fromField.onChange({ target: { name: fromField.name, value } }) }} 
                        />
                        <TextForm
                            textFieldName={messageField.name}
                            templateSelector={(
                                <CategorizedTemplates
                                    onSelectTemplate={handleSelectTemplateChange}
                                    isReadingCategorizedTemplates={isReadingCategorizedTemplates}
                                    type='text'
                                    disabled={isCalendarView}
                                    defaultTemplate={selectedSequence?.action_data?.template}
                                />
                            )}
                            rows={7}
                            loading={isSendingText}
                            attachmentsFieldName={attachmentsField.name}
                            optOutFieldName={'include_opt_out'}
                            disableOptOut={isCalendarView}
                            disabled={isCalendarView}
                            infoIconMessage={`The message ‘${brandOptOutStatus?.opt_out_message}’ will be added at the end of your actual message. This affects the total character count below.`}
                            additionalCharacters={brandOptOutStatus?.opt_out_message?.length}
                            onReplaceVariable={(variable) => {
                                if (variable.includes('My')) {
                                    return convertSenderVariables(variable, userProfile)
                                }
                                return variable;
                            }}
                            isAccount={isAccount}
                        />
                    </Form>
                </FormProvider>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    primary
                    onClick={handleSubmit(handleSendText)}
                    loading={isSendingText}
                    disabled={(!isCalendarView && (!isValid || !isDirty)) || isSendingText || !hasText}
                >
                    Send
                </Button>
            </Modal.Actions>
        </>
    );
}

const TextMessageModal = ({ open, onClose, contact_id, contact_name, isAccount, selectedSequence, onMessageSend }) => {
    const dispatch = useDispatch();
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const { isTextFormDirty , setIsTextFormDirty } = useContext(TextingComponentContext)
    useEffect(() => {
        if (open) {
            dispatch(templateCategoryActions.readCategorizedTemplates({ category: 'text' }));
            dispatch(tcrActions.getBrand()).then((data) => {
                dispatch(tcrActions.getBrandOptOutStatus(data?.brandId))
            })
            if (isAccount) {
                dispatch(textActions.getAccountInfo(contact_id));
            } else {
                dispatch(textActions.getContactInfo(contact_id));
            }
        }
    }, [dispatch, open, contact_id, isAccount]);

    const isReading = useSelector(state => state.text.isReadingContact || state.text.isReadingAccount || state.user.isReadingUser);
    return (
        <>
            <Modal
                open={open}
                className="TextMessageModal"
                onClose={() => {isTextFormDirty ? setIsConfirmationOpen(true) : onClose() }}
                animated={false}
            >
                <Modal.Header>Text Message</Modal.Header>
                {isReading ? <Segment basic loading></Segment> : <TextFormContainer contact_id={contact_id} contact_name={contact_name} isAccount={isAccount} setIsTextFormDirty={setIsTextFormDirty} defaultValues={{
                message: selectedSequence?.action_data?.message || "",
                attachments: selectedSequence?.action_data?.attachments || [],
                include_opt_out: false,
                from:  '',
                to:  ''
            }} selectedSequence={selectedSequence} onMessageSend={onMessageSend} onClose={onClose} />}
            </Modal>
            <FullScreenModalPopUp
                header={"Text will not be saved."}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onClose();
                    setIsConfirmationOpen(false);
                    setIsTextFormDirty(false)
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </>
    );
};


export default TextMessageModal;
